import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton, Grid2 } from '@mui/material';
import { LocationOn, Work, CalendarToday, Person, Business, Phone, Email } from '@mui/icons-material';

import { Rooftop, RooftopAuthorizedEmployer } from '@libs/models';
import { RooftopAuthorizationRequestStatus } from '@libs/types';
import { useRooftopViewQuery } from '@carcareers/store';
import {
  InfoCard,
  Map,
  EmployerRooftopUpdateForm,
  RooftopAuthorizedRequestInput,
  DashboardPageContainer,
} from '@carcareers/components';
import { useAuth } from '@carcareers/hooks';
import { FormProvider } from '@carcareers/contexts';
import { routes, useIsMobile } from '@carcareers/utils';

export const EmployerRooftopView: FC = () => {
  const { id } = useParams<{ id: string }>();
  const rooftopId = id ? parseInt(id) : undefined;
  const navigate = useNavigate();
  const { session } = useAuth();
  const isMobile = useIsMobile();

  const [rooftop, setRooftop] = useState<Rooftop | undefined>(undefined);
  const { data, isLoading } = useRooftopViewQuery(rooftopId ?? 0, { skip: !rooftopId });

  useEffect(() => {
    if (!rooftopId) {
      navigate(routes.EMPLOYER_ROOFTOPS_INDEX);
    }
  }, [rooftopId, navigate]);

  useEffect(() => {
    if (!isLoading && data) {
      setRooftop(data);
    }
  }, [isLoading, data]);

  const isOwner = rooftop?.owner?.user?.id === session?.user?.id;

  const authorizedEmployersCount =
    rooftop?.authorizations?.filter(
      (auth: RooftopAuthorizedEmployer) => auth.status === RooftopAuthorizationRequestStatus.COMPLETED,
    ).length || 0;

  const getInitials = (name?: string) => {
    return (
      name
        ?.split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'RT'
    );
  };

  return (
    <DashboardPageContainer idReplacement={rooftop?.name}>
      <Stack spacing={3}>
        {/* Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading ? '' : getInitials(rooftop?.name)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? <Skeleton variant="text" width={200} height={32} /> : rooftop?.name || 'Unknown Rooftop'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isLoading ? (
                    <Skeleton variant="text" width={150} height={20} />
                  ) : (
                    rooftop?.location || 'Location not specified'
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip icon={<LocationOn />} label={`ID: ${rooftop?.id || '...'}`} variant="outlined" sx={{ px: 1 }} />
                  <Chip
                    icon={<Person />}
                    label={`Owner: ${rooftop?.owner?.user?.firstName || ''} ${rooftop?.owner?.user?.lastName || ''}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={
                      rooftop?.createdAt ? `Created ${new Date(rooftop.createdAt).toLocaleDateString()}` : 'Created ...'
                    }
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Map */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
            '& .MuiCardContent-root': {
              p: '0 !important',
            },
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Map pin disableControls location={rooftop?.location} zoom={14} />
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Work sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : rooftop?.jobs?.length || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Total Jobs
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Business sx={{ color: 'secondary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : authorizedEmployersCount}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Authorized Employers
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Rooftop Details Grid */}
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Email" value={rooftop?.email || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Phone" value={rooftop?.phone || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Location" value={rooftop?.location || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Owner Email" value={rooftop?.owner?.user?.email || 'Not specified'} loading={isLoading} />
          </Grid2>
        </Grid2>

        {/* Rooftop Management */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ color: 'primary.main' }} />
                {isOwner ? 'Rooftop Management' : 'Rooftop Details'}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <FormProvider initialData={rooftop}>
                {isOwner ? (
                  <EmployerRooftopUpdateForm />
                ) : (
                  <Stack spacing={2}>
                    <Typography variant="h6" gutterBottom>
                      Contact Information
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Email color="primary" />
                      <Typography>{rooftop?.email || 'No email provided'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Phone color="primary" />
                      <Typography>{rooftop?.phone || 'No phone provided'}</Typography>
                    </Stack>
                  </Stack>
                )}
              </FormProvider>
            </Box>
          </CardContent>
        </Card>

        {/* Authorized Employers */}
        {rooftop && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ color: 'secondary.main' }} />
                  Authorized Employers
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <RooftopAuthorizedRequestInput rooftop={rooftop} hasActions={isOwner} />
              </Box>
            </CardContent>
          </Card>
        )}
      </Stack>
    </DashboardPageContainer>
  );
};

export default EmployerRooftopView;
